import { formatCanonicalAddressShort } from "@/web/lib/addresses";
import type { Property } from "@tycho/web-bindings/types";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

type CompsUnavailableProps = {
  /** Every targeted property whose comps couldn't be fetched. */
  properties: Property[];
};

const listFormatter = new Intl.ListFormat("en", { style: "long", type: "conjunction" });

function CompsUnavailableAction({
  properties,
  onSkip,
}: CompsUnavailableProps & SmbActionCoreProps) {
  const addresses = listFormatter.format(
    properties.map((p) => formatCanonicalAddressShort(p.address)),
  );
  // All affected properties share the no-results branch only when every one of
  // them is NoResults; any failure flips the card into the error wording.
  const allNoResults = properties.every((p) => p.compsJobStatus?.tag === "NoResults");
  const multiple = properties.length > 1;

  return (
    <ActionCard
      category="negotiate"
      // Copy-independent e2e anchor; distinguishes the FAILED branch from the
      // NoResults branch (both render this card with different copy).
      data-testid={allNoResults ? "comps-no-results" : "comps-unavailable-failed"}
    >
      <ActionCard.Header>
        <ActionCard.Badge>Price</ActionCard.Badge>
      </ActionCard.Header>
      <ActionCard.Main>
        <ActionCard.Title>
          {allNoResults
            ? "We couldn't find comparable properties"
            : "We ran into issues fetching comps"}
        </ActionCard.Title>
        <ActionCard.Description>
          {allNoResults
            ? `We couldn't find comparable properties for ${addresses}.`
            : `We ran into issues fetching comps for ${addresses}.`}{" "}
          Contact us at{" "}
          <a href="mailto:support@with.co" className="underline underline-offset-2">
            support@with.co
          </a>
          {multiple ? " and we'll help with these." : "."}
        </ActionCard.Description>
      </ActionCard.Main>
      <ActionCard.Footer>
        <ActionCard.SkipButton onSkip={onSkip} />
        <ActionCard.PrimaryButton onClick={() => window.location.assign("mailto:support@with.co")}>
          Contact support
        </ActionCard.PrimaryButton>
      </ActionCard.Footer>
    </ActionCard>
  );
}

export function createCompsUnavailableAction(
  props: CompsUnavailableProps & { id?: string },
): SmbActionDefinition {
  const { properties, id = "comps-unavailable" } = props;
  return {
    id,
    category: "negotiate",
    canSkip: true,
    render: (core) => <CompsUnavailableAction properties={properties} {...core} />,
  };
}

// Single card across every targeted property: shows when ANY property's comps
// fetch ended unavailable (no results / failed / timed out), listing them all.
export const compsUnavailableRule = CardRulesEngine.singletonRule({
  id: "comps-unavailable",
  priority: 90,
  when: (d) => d.properties.some((p) => CardRulesEngine.propertyCompsUnavailable(d, p)),
  build: (d) =>
    createCompsUnavailableAction({
      properties: d.properties.filter((p) => CardRulesEngine.propertyCompsUnavailable(d, p)),
    }),
});
